/**
 * @file AccountDeletionPending.jsx
 * @description Full-screen gate shown when a logged-in user has a pending account
 *   deletion request. Lets them reactivate their account or confirm deletion.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/AccountDeletionPending
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { T } from "../theme";

export default function AccountDeletionPending({ profile, onReactivated, onSignOut }) {
  const { t } = useTranslation();
  const [reactivating, setReactivating] = useState(false);
  const [msg, setMsg]                   = useState("");

  const deadline      = profile?.reactivation_token_expires_at
    ? new Date(profile.reactivation_token_expires_at)
    : null;
  const daysLeft      = deadline
    ? Math.max(0, Math.ceil((deadline - Date.now()) / 86400000))
    : null;
  const deadlineStr   = deadline
    ? deadline.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : t('common.soon');

  async function handleReactivate() {
    if (reactivating) return;
    setReactivating(true);
    setMsg("");
    try {
      const token = profile?.reactivation_token;
      if (!token) throw new Error("No reactivation token found.");

      const { data, error } = await supabase.rpc("reactivate_account", { p_token: token });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.reason || "Reactivation failed.");

      // Send reactivation confirmation email (best-effort)
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        supabase.functions.invoke("process-account-deletion", {
          body: { action: "reactivated", user_id: profile.id },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }).catch(() => {});
      }

      setMsg(t("accountDeletion.successMessage"));
      setTimeout(() => onReactivated && onReactivated(), 1500);
    } catch (err) {
      setMsg(err.message || t('common.error'));
    } finally {
      setReactivating(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
    }}>
      <div style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        padding: "40px 36px",
        maxWidth: 500,
        width: "100%",
        textAlign: "center",
        boxShadow: T.shadowMd,
      }}>
        {/* Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "#fef2f2", border: `1px solid #fecaca`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px", fontSize: 28,
        }}>
          🗑️
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: "0 0 10px" }}>
          {t("accountDeletion.heading")}
        </h2>

        <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, margin: "0 0 8px" }}>
          {t("accountDeletion.bodyRequested")}
        </p>

        <div style={{
          fontSize: 15, fontWeight: 700, color: T.red,
          background: "#fef2f2", border: "1px solid #fecaca",
          borderRadius: 8, padding: "8px 16px", display: "inline-block",
          margin: "0 0 16px",
        }}>
          {deadlineStr}
          {daysLeft !== null && (
            <span style={{ fontSize: 12, fontWeight: 400, color: T.red, marginLeft: 8 }}>
              ({t("accountDeletion.daysRemaining", { count: daysLeft })})
            </span>
          )}
        </div>

        <p style={{ fontSize: 13, color: T.textLight, lineHeight: 1.6, margin: "0 0 28px" }}>
          {t("accountDeletion.bodyInstructions", { deadline: deadlineStr })}
        </p>

        {/* Reactivate button */}
        <button
          onClick={handleReactivate}
          disabled={reactivating}
          style={{
            display: "block", width: "100%",
            padding: "12px 0",
            background: reactivating ? T.border : T.green,
            color: reactivating ? T.textLight : "#fff",
            border: "none", borderRadius: 8,
            fontSize: 15, fontWeight: 700,
            cursor: reactivating ? "not-allowed" : "pointer",
            marginBottom: 12,
            transition: "background 0.15s",
          }}
        >
          {reactivating ? t("accountDeletion.buttonReactivateLoading") : t("accountDeletion.buttonReactivate")}
        </button>

        {msg && (
          <p style={{
            fontSize: 13,
            color: msg.includes("reactivated") ? T.green : T.red,
            margin: "0 0 16px",
          }}>
            {msg}
          </p>
        )}

        {/* Proceed with deletion */}
        <p style={{ fontSize: 12, color: T.textXLight, margin: "0 0 4px" }}>
          {t("accountDeletion.confirmDeletion")}
        </p>
        <button
          onClick={onSignOut}
          style={{
            background: "none", border: "none",
            color: T.textLight, fontSize: 13,
            cursor: "pointer", textDecoration: "underline", padding: 0,
          }}
        >
          {t("accountDeletion.buttonSignOut")}
        </button>
      </div>
    </div>
  );
}
